<script setup lang="ts">
import type { ModelValue as TimePickerModelValue } from '@/components/pickerFields/TimePickerField.vue';

import { ref } from 'vue';

import DatePickerField from '@/components/pickerFields/DatePickerField.vue';
import TimePickerField from '@/components/pickerFields/TimePickerField.vue';

export interface DateTimeModel {
  time_earliest_date?: string | null;
  time_earliest_time?: TimePickerModelValue | null;
  time_latest_date?: string | null;
  time_latest_time?: TimePickerModelValue | null;
  state?: string | null;
}

export interface Props {
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
});

const model = defineModel<DateTimeModel>({ default: () => ({}) });

const isDateLinked = ref<boolean>(
  !!model.value.time_earliest_date &&
    model.value.time_earliest_date === model.value.time_latest_date,
);

function handleEarliestDateChange(value: string | null | undefined): void {
  const normalized = value ?? null;
  model.value = {
    ...model.value,
    time_earliest_date: normalized,
    ...(isDateLinked.value ? { time_latest_date: normalized } : {}),
  };
}

function handleLatestDateChange(value: string | null | undefined): void {
  const normalized = value ?? null;
  model.value = {
    ...model.value,
    time_latest_date: normalized,
    ...(isDateLinked.value ? { time_earliest_date: normalized } : {}),
  };
}

function handleLinkToggle(): void {
  isDateLinked.value = !isDateLinked.value;
  if (isDateLinked.value) {
    model.value = {
      ...model.value,
      time_latest_date: model.value.time_earliest_date ?? null,
    };
  }
}
</script>

<template>
  <fieldset
    :class="$style.stopDateTimeFieldset"
    data-testid="stopDateTimeFieldset"
  >
    <h5 :class="$style.heading">
      Date and times ({{ model.state || 'NSW' }} local time)
    </h5>

    <div :class="$style.timeGrid">
      <div :class="$style.linkColumn">
        <div :class="$style.connectorSegment" />
        <v-btn
          :icon="isDateLinked ? 'mdi-link-variant' : 'mdi-link-variant-off'"
          size="x-small"
          variant="tonal"
          :color="isDateLinked ? 'primary' : undefined"
          :disabled="props.readonly"
          :class="$style.linkBtn"
          @click="handleLinkToggle"
        />
        <div :class="$style.connectorSegment" />
      </div>

      <div :class="$style.fieldsColumn">
        <div :class="$style.timeRow">
          <TimePickerField
            v-model="model.time_earliest_time"
            label="Earliest time"
            :readonly="props.readonly"
            :class="$style.timePicker"
          />
          <DatePickerField
            :model-value="model.time_earliest_date ?? null"
            label="Earliest date"
            :readonly="props.readonly"
            :class="$style.datePicker"
            @update:model-value="handleEarliestDateChange"
          />
        </div>

        <div :class="$style.timeRow">
          <TimePickerField
            v-model="model.time_latest_time"
            label="Latest time"
            :readonly="props.readonly"
            :class="$style.timePicker"
          />
          <DatePickerField
            :model-value="model.time_latest_date ?? null"
            label="Latest date"
            :readonly="props.readonly"
            :class="$style.datePicker"
            @update:model-value="handleLatestDateChange"
          />
        </div>
      </div>
    </div>
  </fieldset>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

.stopDateTimeFieldset {
  border: none;
  padding: 0;
  margin-bottom: 24px;
}

.heading {
  font-weight: 500;
  font-size: 16px;
  margin-bottom: 12px;
  color: colors.$color-text-primary;
}

.timeGrid {
  display: flex;
  align-items: stretch;
  gap: 4px;
  margin-bottom: 8px;
}

.linkColumn {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 36px;
  flex-shrink: 0;
}

.connectorSegment {
  flex: 1;
  width: 1px;
  background-color: colors.$color-gray-200;
}

.linkBtn {
  flex-shrink: 0;
  margin: 4px 0;
}

.fieldsColumn {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.timeRow {
  display: flex;
  gap: 12px;
}

.timePicker {
  flex: 2;
}

.datePicker {
  flex: 3;
}

</style>
